/**
 * Store de Autenticación - NUEVO MODELO
 * Login con email, roles dinámicos, sin cuentas de iglesia
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "@/lib/firebase";
import { roleService } from "@/lib/services/roles";
import { memberService } from "@/lib/services/members";
import type {
  SystemUser,
  RoleAssignment,
  SystemRole,
  ClubInfo,
  FirebaseAuthError,
} from "@/types";

// ============================================
// TIPOS
// ============================================

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

/**
 * Info resumida de una membresía para el perfil de usuario
 */
export interface MembershipInfo {
  memberId: string;
  clubId: string;
  clubName?: string;
  /** Sección del miembro (derivada de edad) */
  section?: string;
  /** @deprecated Usar section */
  clubType?: string;
  role: string;
  clase?: string;
}

/**
 * Perfil de sesión del usuario
 */
export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  photoUrl?: string;
  phone?: string;
  status: "active" | "inactive" | "pending";
  personId?: string;
  /** Rol principal (el de mayor jerarquía) */
  primaryRole?: SystemRole;
  /** Todas las asignaciones de rol activas */
  roleAssignments: RoleAssignment[];
  /** Si es director, info del club */
  directorOfClub?: ClubInfo;
  /**
   * Si es director de una sección específica del club, qué sección.
   *
   * - undefined → director general del club (ve todo) o no es director.
   * - definido  → director SOLO de esa sección (UI debe filtrar members
   *   por `section==X || staffSection==X`).
   *
   * Derivado de `primaryRoleAssignment.section` cuando role==='club_director'.
   */
  directorOfSection?: import("@/types").ClubType;
  /** Scope del rol principal */
  scopeId?: string;
  scopeName?: string;
  scopeType?: "union" | "association" | "club";
  /** ID de la asociación (para admins de asociación o directores) */
  associationId?: string;
  /** Membresías del usuario en diferentes clubes */
  memberships?: MembershipInfo[];
}

/**
 * Datos de consentimiento legal recolectados durante el registro.
 * Se almacenan junto al documento del usuario como evidencia de aceptación
 * (Habeas Data - Ley 1581/2012 Colombia).
 */
export interface LegalConsent {
  termsVersion: string;
  privacyVersion: string;
  acceptedFromUrl: string;
  userAgent: string;
}

interface AuthState {
  user: AuthUser | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  emailNotVerified: boolean;
  /** Usuario necesita completar registro (sin Person ni solicitud pendiente) */
  needsSetup: boolean;
  /** F12-P1: solicitud aprobada pero la ficha pertenece a otra cuenta. */
  approvedButUnlinked: boolean;
  /** Usuario tiene solicitud pendiente de aprobación (miembro) */
  pendingApproval: boolean;
  /** Usuario tiene solicitud de club pendiente de aprobación (director) */
  pendingClubApproval: boolean;

  // Actions
  initialize: () => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    consent?: LegalConsent,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  clearError: () => void;
  setProfile: (profile: UserProfile) => void;
  refreshRoles: () => Promise<void>;
}

// ============================================
// HELPERS
// ============================================

/**
 * Crea o actualiza el documento del usuario en Firestore
 * Si el usuario ya existe y tiene status "pending" pero el email está verificado,
 * lo activa automáticamente.
 * También vincula automáticamente los miembros registrados con ese email.
 */
async function ensureUserDocument(
  uid: string,
  email: string,
  displayName: string,
  emailVerified: boolean = false,
  consent?: LegalConsent,
): Promise<SystemUser> {
  const userRef = doc(db, "users", uid);
  const userDoc = await getDoc(userRef);

  // ── PASO 0: refrescar el ID token si el email fue verificado.
  // Las reglas de Firestore exigen `request.auth.token.email_verified == true`
  // para promover el /users doc de "pending" a "active". Si el email se
  // acaba de verificar (o fue verificado por admin vía scripts), el token del
  // cliente puede seguir con `email_verified=false` hasta ~1h. Forzar
  // getIdToken(true) sincroniza las claims antes del setDoc de PASO 1.
  if (emailVerified && auth.currentUser) {
    try {
      await auth.currentUser.getIdToken(true);
    } catch (err) {
      console.warn("No se pudo refrescar ID token:", err);
    }
  }

  // ── PASO 1: asegurar que el user document existe y tiene el status correcto
  // ANTES de intentar vincular Persons.
  // Las reglas de Firestore exigen status='active' para escribir en /persons,
  // así que primero promovemos el user si corresponde.
  let activeUserData: SystemUser | null = null;
  if (userDoc.exists()) {
    const userData = { id: userDoc.id, ...userDoc.data() } as SystemUser;
    if (emailVerified && userData.status === "pending") {
      await setDoc(
        userRef,
        { status: "active", updatedAt: serverTimestamp() },
        { merge: true },
      );
      activeUserData = { ...userData, status: "active" };
    } else {
      activeUserData = userData;
    }
  } else {
    // Crear documento. Si emailVerified, queda 'active' inmediatamente y
    // entonces puede vincular Persons en el siguiente paso.
    const newUser: Omit<SystemUser, "id"> = {
      email: email.toLowerCase(),
      displayName,
      status: emailVerified ? "active" : "pending",
      createdAt:
        serverTimestamp() as unknown as import("firebase/firestore").Timestamp,
      updatedAt:
        serverTimestamp() as unknown as import("firebase/firestore").Timestamp,
    };
    // Adjuntar consentimiento legal (Habeas Data Ley 1581/2012) si se provee.
    // Se guarda con cast para no acoplar el type SystemUser a este detalle.
    const docPayload: Record<string, unknown> = { ...newUser };
    if (consent) {
      docPayload.legalConsent = {
        ...consent,
        acceptedAt: serverTimestamp(),
      };
    }
    await setDoc(userRef, docPayload);
    activeUserData = { id: uid, ...newUser } as SystemUser;
  }

  // ── PASO 2: vincular Persons existentes con este email al usuario.
  //
  // Estrategia híbrida (Fase 7+):
  //   - El trigger del servidor `onUserCreated` (Cloud Function) se encarga
  //     de la vinculación masiva con name-similarity check + búsqueda en
  //     `_legacyEmail`. Es la fuente de verdad y NO se debe duplicar aquí.
  //   - El cliente solo hace SELF-HEALING controlado: si encuentra UNA persona
  //     por email cuyo nombre coincide razonablemente, la auto-vincula.
  //     Esto cubre usuarios pre-existentes (creados antes del trigger) y
  //     reduce latencia perceptible para el usuario que recién se registra.
  //   - Si el nombre NO coincide, NO se vincula desde el cliente. Se confía
  //     en el trigger del servidor para decidir entre linked/guardian.
  if (activeUserData.status === "active") {
    try {
      const linkedCount = await memberService.linkAllByEmailToUser(
        email,
        uid,
        displayName,
      );
      if (linkedCount > 0) {
        console.log(
          `✅ ${linkedCount} persona(s) vinculada(s) al usuario ${email}`,
        );
      }
    } catch (error) {
      console.error("Error al vincular Persons por email:", error);
      // No bloqueamos: el trigger del servidor cubrirá los casos restantes.
    }
  }

  // ── PASO 3: resolver personId vinculado para sincronizar SystemUser.personId
  let linkedPersonId: string | undefined;
  try {
    const { personService } = await import("@/lib/services/persons");
    const person = await personService.getByAuthUserId(uid, email);
    // F12-QW8: `getByAuthUserId` cae a búsqueda por EMAIL, que en una familia
    // con correo compartido devuelve la ficha de otra persona. Solo se
    // sincroniza `users.personId` cuando la ficha es REALMENTE de esta cuenta
    // (linkedUserId === uid); si no, se deja sin personId y el self-healing de
    // abajo decide (con validación de nombre) si corresponde vincularla.
    linkedPersonId = person?.linkedUserId === uid ? person.id : undefined;

    // SELF-HEALING: si encontramos Person por email pero NO está vinculada
    // todavía (linkedUserId vacío), validamos similitud de nombre antes de
    // vincular. Si no coincide, dejamos el caso al trigger del servidor.
    if (
      person &&
      !person.linkedUserId &&
      !person.guardianUserId &&
      activeUserData.status === "active"
    ) {
      try {
        await personService.linkToUser(person.id, uid, undefined, displayName);
        console.log(`✅ Person ${person.id} re-vinculada a uid=${uid}`);
        linkedPersonId = person.id; // ya es de esta cuenta: sí se sincroniza
      } catch (linkErr) {
        console.warn(
          `[self-healing] No se vinculó Person ${person.id}:`,
          linkErr,
        );
        // Continuar sin bloquear: el trigger del servidor evaluará el caso.
      }
    }
  } catch (error) {
    console.error("Error al buscar Person vinculada:", error);
  }

  // ── PASO 4: sincronizar personId en SystemUser si cambió
  if (linkedPersonId && activeUserData.personId !== linkedPersonId) {
    await setDoc(
      userRef,
      { personId: linkedPersonId, updatedAt: serverTimestamp() },
      { merge: true },
    );
    activeUserData = { ...activeUserData, personId: linkedPersonId };
  }

  return activeUserData;
}

/**
 * Construye el perfil de sesión a partir del usuario y sus roles
 */
async function buildUserProfile(systemUser: SystemUser): Promise<UserProfile> {
  // Obtener roles del usuario
  const roleAssignments = await roleService.getRolesByUserId(systemUser.id);
  const primaryRoleAssignment =
    roleAssignments.length > 0
      ? await roleService.getPrimaryRole(systemUser.id)
      : null;

  // Si es director, obtener info del club
  let directorOfClub: ClubInfo | undefined;
  if (primaryRoleAssignment?.role === "club_director") {
    const clubDoc = await getDoc(
      doc(db, "clubs", primaryRoleAssignment.scopeId),
    );
    if (clubDoc.exists()) {
      const clubData = clubDoc.data();
      directorOfClub = {
        id: clubDoc.id,
        name: clubData.name,
        type: clubData.type,
        sections: clubData.sections,
        status: clubData.status,
        memberCount: clubData.memberCount,
        associationId: clubData.associationId,
        logoUrl: clubData.logoUrl,
        inviteCode: clubData.inviteCode,
      };
    }
  }

  // Sincronizar foto del usuario con Person vinculado
  // Si el usuario tiene foto y el Person no, la sincronizamos
  if (systemUser.photoUrl) {
    try {
      const { personService } = await import("@/lib/services/persons");
      await personService.syncUserPhoto(systemUser.id, systemUser.photoUrl);
    } catch (error) {
      console.error("Error sincronizando foto de usuario:", error);
    }
  }

  // Obtener membresías del usuario
  let memberships: MembershipInfo[] = [];
  try {
    const userMemberships = await memberService.getByLinkedUser(systemUser.id);

    // Obtener nombres de clubes
    const clubIds = [...new Set(userMemberships.map((m) => m.clubId))];
    const clubDocs = await Promise.all(
      clubIds.map((id) => getDoc(doc(db, "clubs", id))),
    );
    const clubNames: Record<string, string> = {};
    clubDocs.forEach((d) => {
      if (d.exists()) {
        clubNames[d.id] = d.data().name;
      }
    });

    memberships = userMemberships.map((m) => ({
      memberId: m.id,
      clubId: m.clubId,
      clubName: clubNames[m.clubId],
      section: m.section,
      role: m.role,
      clase: m.clase,
    }));
  } catch (error) {
    console.error("Error al cargar membresías:", error);
  }

  // Si no tiene RoleAssignment pero sí membresías, derivar rol "member"
  const derivedRole: SystemRole | undefined =
    primaryRoleAssignment?.role ??
    (memberships.length > 0 ? "member" : undefined);

  return {
    id: systemUser.id,
    email: systemUser.email,
    displayName: systemUser.displayName,
    photoUrl: systemUser.photoUrl,
    phone: systemUser.phone,
    status: systemUser.status,
    personId: systemUser.personId,
    primaryRole: derivedRole,
    roleAssignments,
    directorOfClub,
    directorOfSection:
      primaryRoleAssignment?.role === "club_director"
        ? (primaryRoleAssignment.section ?? undefined)
        : undefined,
    scopeId: primaryRoleAssignment?.scopeId,
    scopeName: primaryRoleAssignment?.scopeName,
    scopeType: primaryRoleAssignment?.scopeType,
    // Para admins de asociación scopeId es la asociación, para directores viene del club
    associationId:
      primaryRoleAssignment?.scopeType === "association"
        ? primaryRoleAssignment?.scopeId
        : directorOfClub?.associationId,
    memberships,
  };
}

// ============================================
// STORE
// ============================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      loading: true,
      error: null,
      emailNotVerified: false,
      needsSetup: false,
      approvedButUnlinked: false,
      pendingApproval: false,
      pendingClubApproval: false,

      initialize: () => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser && firebaseUser.email) {
            // Verificar si el email está verificado
            if (!firebaseUser.emailVerified) {
              set({
                user: null,
                profile: null,
                loading: false,
                emailNotVerified: true,
              });
              return;
            }

            const authUser: AuthUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
            };

            try {
              // Obtener o crear documento del usuario
              // Pasar emailVerified para auto-activar si el email está verificado
              const systemUser = await ensureUserDocument(
                firebaseUser.uid,
                firebaseUser.email,
                firebaseUser.displayName || firebaseUser.email.split("@")[0],
                firebaseUser.emailVerified,
              );

              // Construir perfil con roles
              const profile = await buildUserProfile(systemUser);

              // Detectar si el usuario necesita completar registro
              const hasRole = !!profile.primaryRole;
              const hasMembership = (profile.memberships?.length ?? 0) > 0;
              let needsSetup = false;
              let pendingApproval = false;
              let pendingClubApproval = false;
              // F12-P1: aprobada pero sin membresía visible (ficha en otra cuenta)
              let approvedButUnlinked = false;

              // Un ACUDIENTE puede no ser miembro de nada y aun así tener que
              // entrar: sus hijos están inscritos a su cargo y es quien debe
              // subirles los documentos del evento. Mandarlo al wizard de
              // "únete a un club" lo dejaba encerrado sin poder llegar al
              // dashboard, que es donde vive la tarjeta de menores a cargo.
              let tieneMenoresACargo = false;
              if (!hasRole && !hasMembership) {
                try {
                  const { getWardsOf } = await import("@/lib/services/persons");
                  tieneMenoresACargo = (await getWardsOf(systemUser.id)).length > 0;
                } catch (e) {
                  console.warn("[initialize] no se pudieron leer los menores a cargo:", e);
                }
              }

              if (!hasRole && !hasMembership && !tieneMenoresACargo) {
                // Sin rol ni membresía: verificar si tiene solicitud pendiente
                const { joinRequestService } =
                  await import("@/lib/services/joinRequests");
                const latestRequest =
                  await joinRequestService.getUserLatestRequest(systemUser.id);
                if (latestRequest) {
                  // pending o rejected deben ir a /pending-approval
                  pendingApproval = true;
                } else {
                  // Verificar solicitud de club pendiente
                  const { getUserPendingRequest: getClubPending } =
                    await import("@/lib/services/clubApprovalRequests");
                  const clubPending = await getClubPending(systemUser.id);
                  if (clubPending) {
                    pendingClubApproval = true;
                  } else {
                    // F12-P1: antes se caía directo al wizard. Si la persona
                    // YA tiene una solicitud APROBADA pero no ve membresía, el
                    // problema no es que le falten datos: es que su ficha
                    // quedó vinculada a otra cuenta (correo anterior o de un
                    // familiar). Volver a pedirle el formulario no arregla
                    // nada y genera solicitudes duplicadas — se le explica.
                    // Si la consulta falla, se mantiene el comportamiento
                    // actual (wizard) para no dejar a nadie sin camino.
                    let approved = null;
                    try {
                      approved =
                        await joinRequestService.getUserApprovedRequest(
                          systemUser.id,
                        );
                    } catch (e) {
                      console.warn("[initialize] no se pudo leer la solicitud aprobada:", e);
                    }
                    if (approved) {
                      approvedButUnlinked = true;
                    } else {
                      needsSetup = true;
                    }
                  }
                }
              }

              set({
                user: authUser,
                profile,
                loading: false,
                error: null,
                needsSetup,
                pendingApproval,
                pendingClubApproval,
                approvedButUnlinked,
              });
            } catch (error) {
              console.error("Error loading user profile:", error);
              set({
                user: authUser,
                profile: null,
                loading: false,
                error: "Error al cargar el perfil",
              });
            }
          } else {
            set({
              user: null,
              profile: null,
              loading: false,
            });
          }
        });

        return unsubscribe;
      },

      signIn: async (email: string, password: string) => {
        set({ loading: true, error: null, emailNotVerified: false });
        try {
          const credential = await signInWithEmailAndPassword(
            auth,
            email,
            password,
          );

          // Verificar si el email está verificado
          if (!credential.user.emailVerified) {
            set({
              loading: false,
              emailNotVerified: true,
              error: "Debes verificar tu correo electrónico para continuar",
            });
            // NO cerrar sesión: mantenemos auth.currentUser para
            // poder hacer reload() al verificar email
            throw new Error("EMAIL_NOT_VERIFIED");
          }

          // El perfil se carga automáticamente en onAuthStateChanged
        } catch (error) {
          const authError = error as FirebaseAuthError;

          // Si es nuestro error de verificación, re-lanzar
          if (
            error instanceof Error &&
            error.message === "EMAIL_NOT_VERIFIED"
          ) {
            throw error;
          }

          let errorMessage = "Error al iniciar sesión";

          switch (authError.code) {
            case "auth/invalid-email":
              errorMessage = "Email inválido";
              break;
            case "auth/user-disabled":
              errorMessage = "Cuenta deshabilitada";
              break;
            case "auth/user-not-found":
              errorMessage = "Usuario no encontrado";
              break;
            case "auth/wrong-password":
            case "auth/invalid-credential":
              errorMessage = "Contraseña incorrecta";
              break;
            case "auth/too-many-requests":
              errorMessage = "Demasiados intentos. Intenta más tarde.";
              break;
          }

          set({ error: errorMessage, loading: false });
          throw new Error(errorMessage);
        }
      },

      signUp: async (
        email: string,
        password: string,
        displayName: string,
        consent?: LegalConsent,
      ) => {
        set({ loading: true, error: null });
        try {
          const credential = await createUserWithEmailAndPassword(
            auth,
            email,
            password,
          );

          // Actualizar displayName en Firebase Auth
          await updateProfile(credential.user, { displayName });

          // Enviar email de verificación con plantilla HTML personalizada
          // Si la Cloud Function falla (aún no desplegada), usa Firebase Auth como fallback
          const pendingInviteCode = sessionStorage.getItem("pending_invite");
          const verifyBaseUrl = `${window.location.origin}/verify-email`;
          const verifyRedirectUrl = pendingInviteCode
            ? `${verifyBaseUrl}?invite=${encodeURIComponent(pendingInviteCode)}`
            : verifyBaseUrl;
          try {
            const sendVerificationEmailFn = httpsCallable(
              functions,
              "sendVerificationEmail",
            );
            await sendVerificationEmailFn({
              email,
              displayName,
              redirectUrl: verifyRedirectUrl,
            });
          } catch (emailErr) {
            console.warn(
              "Cloud Function de email no disponible, usando Firebase Auth:",
              emailErr,
            );
            const actionCodeSettings = {
              url: verifyRedirectUrl,
              handleCodeInApp: true,
            };
            await sendEmailVerification(credential.user, actionCodeSettings);
          }

          // Crear documento en Firestore (status pending hasta verificar email)
          // El email aún no está verificado, así que se crea como pending
          await ensureUserDocument(
            credential.user.uid,
            email,
            displayName,
            false,
            consent,
          );

          // NO cerrar sesión: mantenemos auth.currentUser para poder
          // hacer reload() sin re-pedir credenciales al verificar email
          set({
            loading: false,
            emailNotVerified: true,
          });
        } catch (error) {
          const authError = error as FirebaseAuthError;
          let errorMessage = "Error al crear cuenta";

          switch (authError.code) {
            case "auth/email-already-in-use":
              errorMessage = "Este email ya está registrado";
              break;
            case "auth/invalid-email":
              errorMessage = "Email inválido";
              break;
            case "auth/weak-password":
              errorMessage = "La contraseña debe tener al menos 6 caracteres";
              break;
          }

          set({ error: errorMessage, loading: false });
          throw new Error(errorMessage);
        }
      },

      resendVerificationEmail: async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error("No hay usuario autenticado");
        }

        try {
          const pendingInviteCode = sessionStorage.getItem("pending_invite");
          const verifyBaseUrl = `${window.location.origin}/verify-email`;
          const verifyRedirectUrl = pendingInviteCode
            ? `${verifyBaseUrl}?invite=${encodeURIComponent(pendingInviteCode)}`
            : verifyBaseUrl;
          const sendVerificationEmailFn = httpsCallable(
            functions,
            "sendVerificationEmail",
          );
          await sendVerificationEmailFn({
            email: currentUser.email,
            displayName: currentUser.displayName ?? undefined,
            redirectUrl: verifyRedirectUrl,
          });
        } catch (fnError: any) {
          // Si la Cloud Function está configurada pero algo salió mal, propagar el error
          if (fnError?.code === "functions/failed-precondition") {
            // SMTP no configurado — usar fallback de Firebase Auth
            console.warn(
              "SMTP no configurado, usando Firebase Auth como fallback:",
              fnError,
            );
            const pendingInviteCode = sessionStorage.getItem("pending_invite");
            const verifyBaseUrl = `${window.location.origin}/verify-email`;
            const verifyRedirectUrl = pendingInviteCode
              ? `${verifyBaseUrl}?invite=${encodeURIComponent(pendingInviteCode)}`
              : verifyBaseUrl;
            try {
              const actionCodeSettings = {
                url: verifyRedirectUrl,
                handleCodeInApp: true,
              };
              await sendEmailVerification(currentUser, actionCodeSettings);
            } catch (authError: any) {
              if (authError.code === "auth/too-many-requests") {
                throw new Error("Demasiados intentos. Espera unos minutos.");
              }
              throw new Error("Error al enviar el correo de verificación");
            }
          } else if (fnError?.code === "functions/not-found") {
            // Function aún no desplegada — usar Firebase Auth
            const pendingInviteCode = sessionStorage.getItem("pending_invite");
            const verifyBaseUrl = `${window.location.origin}/verify-email`;
            const verifyRedirectUrl = pendingInviteCode
              ? `${verifyBaseUrl}?invite=${encodeURIComponent(pendingInviteCode)}`
              : verifyBaseUrl;
            const actionCodeSettings = {
              url: verifyRedirectUrl,
              handleCodeInApp: true,
            };
            await sendEmailVerification(currentUser, actionCodeSettings);
          } else {
            throw new Error("Error al enviar el correo de verificación");
          }
        }
      },

      signOut: async () => {
        set({ loading: true });
        try {
          await firebaseSignOut(auth);
          set({
            user: null,
            profile: null,
            loading: false,
          });
        } catch (error) {
          console.error("Error signing out:", error);
          set({ loading: false });
        }
      },

      logout: async () => {
        await get().signOut();
      },

      resetPassword: async (email: string) => {
        set({ loading: true, error: null });
        try {
          await sendPasswordResetEmail(auth, email);
          set({ loading: false });
        } catch (error) {
          const authError = error as FirebaseAuthError;
          let errorMessage = "Error al enviar email";

          if (authError.code === "auth/user-not-found") {
            errorMessage = "No existe usuario con ese email";
          }

          set({ error: errorMessage, loading: false });
          throw new Error(errorMessage);
        }
      },

      clearError: () => set({ error: null, emailNotVerified: false }),

      setProfile: (newProfile: UserProfile) => {
        set({ profile: newProfile });
      },

      refreshRoles: async () => {
        const { profile } = get();
        if (!profile) return;

        try {
          // Obtener el systemUser actualizado
          const userDoc = await getDoc(doc(db, "users", profile.id));
          if (userDoc.exists()) {
            const systemUser = {
              id: userDoc.id,
              ...userDoc.data(),
            } as SystemUser;
            const newProfile = await buildUserProfile(systemUser);
            set({ profile: newProfile });
          }
        } catch (error) {
          console.error("Error refreshing roles:", error);
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
      }),
    },
  ),
);
